import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { environment } from '../../environments/environment';
import { DatabaseService } from './db-service';
import { DocumentReference, User, FestivalUser, AgeGroup } from '../models';

@Injectable({
  providedIn: 'root'
})

export class FestivalUserService {

    // Keeping collection name as userGroups to avoid affecting existing reports
    // We can use new collection just by updating this variable 
    public collection: string = 'userGroups'; // 'festivalUser'
    private currentFestivalUser: FestivalUser = null;

    constructor(
        private afstore: AngularFirestore,
        private databaseService: DatabaseService) {
    }

    public SetCurrentFestivalUser = (currentFestivalUser: FestivalUser): void => {
        this.currentFestivalUser = currentFestivalUser;
    } 

    public GetCurrentFestivalUser = (): FestivalUser => {
        return this.currentFestivalUser;
    }

    /**
     * Get User Group from edit
     * @param userId reference of user
     */
    public async getFestivalUser(userId: string) {
        return this.databaseService.getDocuments(this.collection, ref => ref
            .where('serviceYear', '==', environment.serviceYear)
            .where('userId', '==', userId)
            .where('isActive', '==', true)
            .where('isDeleted', '==', false)
        );
    }

    /**
     * Initiate the login process for this user
     * @param isActive indicates that user is Active
     * @param isDeleted indicates that user is Deleted
     */
    public getFestivalUserList(isActive: boolean, isDeleted: boolean, churchRef: DocumentReference, serviceYear = environment.serviceYear, exlcudePriests = false) {
        if (exlcudePriests) {
            return this.databaseService.getDocuments(this.collection, ref => ref
                .where('serviceYear', '==', serviceYear)
                .where('role', '==', 'Servant')
                .where('church', '==', churchRef)
                .where('isActive', '==', isActive)
                .where('isDeleted', '==', isDeleted)
                .orderBy('user')
            );
        }
        return this.databaseService.getDocuments(this.collection, ref => ref
            .where('serviceYear', '==', serviceYear)
            .where('church', '==', churchRef)
            .where('isActive', '==', isActive)
            .where('isDeleted', '==', isDeleted)
            .orderBy('user')
        );
    }

    public orderByUserName( a: FestivalUser, b: FestivalUser ) {
        if ( a.user.displayName < b.user.displayName ) {
          return -1;
        }
        if ( a.user.displayName > b.user.displayName ) {
          return 1;
        }
        return 0;
    }

    /**
     * @param church is user church
     */
    public getPriests(isActive: boolean = true, isDeleted: boolean = false, serviceYear = environment.serviceYear) {
        return this.databaseService.getDocuments(this.collection, ref => ref
            .where('serviceYear', '==', serviceYear)
            .where('role', '==', 'Priest')
            .where('isActive', '==', isActive)
            .where('isDeleted', '==', isDeleted)
        );
    }

    /**
     * @param church is user church
     */
    public async getApprovers(churchRef: DocumentReference) {
        return this.databaseService.getDocuments(this.collection, ref => ref
            .where('serviceYear', '==', environment.serviceYear)
            .where('church', '==', churchRef)
            .where('isChurchApprover', '==', true)
            .where('isActive', '==', true)
            .where('isDeleted', '==', false)
        );
    }

    public async getActivityApprovers(churchRef: DocumentReference) {
        return this.databaseService.getDocuments(this.collection, ref => ref
            .where('serviceYear', '==', environment.serviceYear)
            .where('church', '==', churchRef)
            .where('isActivityApprover', '==', true)
            .where('isActive', '==', true)
            .where('isDeleted', '==', false)
        );
    }

    // CRUD functions
    // Create
    public async createFestivalUser(festivalUser: FestivalUser, user: User, userId: string) {
        festivalUser.user = {
            reference: this.databaseService.doc(`users/${userId}`).ref,
            displayName: `${user.firstName} ${user.lastName}`
        };

        festivalUser.userId = userId;
        festivalUser.serviceYear = environment.serviceYear;
        festivalUser.lastPasswordResetDate = new Date();
        this.afstore
                .collection(this.collection)
                .add(festivalUser);
    }

    public async migrateFestivalUser(festivalUser: FestivalUser, resetPermissions: boolean, ageLevelList: AgeGroup[]) {
        festivalUser.serviceYear = environment.serviceYear;
        festivalUser.ageGroupList.forEach(ageGroup => {
            let ageGroupDisplayName = ageLevelList.find(ag => ag.id == ageGroup.id).displayName;
            ageGroup.displayName = ageGroupDisplayName;
        });

        const id = this.afstore.createId();
        festivalUser.id = id;
        
        if (resetPermissions) {
            festivalUser = {
                ...festivalUser,
                isAdmin: false,
                isReportViewer: false,
                isMemberCoordinator: false,
                isFinanceCoordinator: false,
                isSportLeader: false,
                isChurchApprover: false,
                isSportCoordinator: false,
                sportList: [],
                activityList: [],
                isActivityLeader: false,
                isActivityCoordinator: false,
                isActivityApprover: false
            };
        }
        
        this.afstore
                .collection(this.collection)
                .doc(id)
                .set(festivalUser);
    }

    public deleteFestivalUser(userId: string) {
        return this.afstore
            .collection(this.collection)
            .doc(userId)
            .set({ isDeleted: true }, { merge: true });
    }

    public toDocumentReference(festivalUser: FestivalUser) {
        return {
            reference: this.databaseService.doc(`${this.collection}/${festivalUser.id}`).ref,
            displayName: festivalUser.user.displayName
        }
    }
}
